import {
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  getSessionDateLabel,
  getSessionTimeLabel,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
  formatDateParts,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import ContentEditable from './content-editable';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import FooterWidget from './widgets/footer-widget';
import { templateStaticDefaults } from './template-defaults';
import { BLOCK_ID } from './widgets/block-ids';
import { BlockRenderer } from './widgets/block-renderer';
import StyledBlockBox from './widgets/styled-block-box';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';

const DEFAULTS = templateStaticDefaults.palais;

const PALAIS_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.topBar]: { background: 'var(--container)', height: 20 },
  [BLOCK_ID.divider1]: { color: 'var(--heading)', padding: '0 56px', opacity: 1, thickness: 2 },
  [BLOCK_ID.salutation]: {
    color: 'var(--heading)',
    fontFamily: 'var(--body-font)',
    fontSize: '16px',
    fontWeight: 400,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    padding: '16px 56px',
  },
  [BLOCK_ID.divider2]: { color: 'var(--heading)', padding: '0 56px', opacity: 1, thickness: 2 },
  [BLOCK_ID.title]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '52px',
    fontWeight: 400,
    fontStyle: 'italic',
    lineHeight: '57px',
    padding: '20px 56px',
  },
  [BLOCK_ID.mainImage]: {
    size: 'original',
    width: 244,
    height: 306,
    borderRadius: 'var(--image-radius)',
    padding: '12px 56px',
    align: 'left',
  },
  [BLOCK_ID.divider3]: { color: 'var(--accent)', padding: '0 56px', thickness: 2 },
  [BLOCK_ID.cta]: {
    align: 'left',
    tdPadding: '12px 24px',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    border: '2px',
    borderColor: 'var(--accent)',
    fontFamily: 'var(--body-font)',
    fontWeight: 700,
    textTransform: 'uppercase',
    fontSize: '16px',
  },
  [BLOCK_ID.description]: {
    color: 'var(--body)',
    fontFamily: 'var(--body-font)',
    fontSize: '14px',
    fontStyle: 'italic',
    lineHeight: '22px',
    padding: '32px 56px',
  },
  [BLOCK_ID.divider4]: { color: 'var(--heading)', padding: '0 56px', opacity: 0.3, thickness: 2 },
  [BLOCK_ID.tagline]: {
    color: 'var(--heading)',
    fontFamily: 'var(--body-font)',
    fontSize: '12px',
    textTransform: 'uppercase',
    textAlign: 'center',
    padding: '8px 56px 40px 56px',
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

const INFO_CARD_BLOCK_ID = BLOCK_ID.infoCard;
const TOP_BAR_BLOCK_ID: string = BLOCK_ID.topBar;

export const Palais = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '0px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '12px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionEndTimestamp = event.sessions[0]?.end_timestamp;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const durationMinutes =
    sessionTimestamp && sessionEndTimestamp ? Math.round((sessionEndTimestamp - sessionTimestamp) / 60000) : null;
  const anchorHref = href || '#';
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Libre Caslon Text", Georgia, serif'
  );
  const bodyFont = resolveThemedFont(
    customizations?.bodyFont,
    theme?.font_pack?.body_name,
    '"EB Garamond", Georgia, serif'
  );
  const _salutation = salutation ?? (customizations?.salutation || DEFAULTS.salutation);
  const eventTitle = title ?? (customizations?.title || DEFAULTS.title);
  const eventDescription = description || customizations?.description || DEFAULTS.description;
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  const footerTextColor = body;
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const topBarBlock = blocks.find((b) => b.id === TOP_BAR_BLOCK_ID);
    const primaryBlocks = blocks.filter((b) => b.id !== TOP_BAR_BLOCK_ID);
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: PALAIS_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };
    const infoRowStyle = {
      color: 'var(--heading)',
      fontSize: '18px',
      fontFamily: 'var(--body-font)',
      paddingBottom: '16px',
    };

    return (
      <table
        data-component="Palais"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--accent': accent,
            '--button-color': buttonColor,
            '--heading': heading,
            '--body': body,
            '--button-text': buttonText,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {topBarBlock && (
                    <tr>
                      <td>
                        <BlockShell
                          block={topBarBlock}
                          isEditMode={!!isEditMode}
                          reorderable={false}
                          onSelect={onSelectBlock}
                          onDelete={onDeleteBlock}
                        >
                          <BlockRenderer block={topBarBlock} stylePreset={PALAIS_BLOCK_PRESETS[topBarBlock.id]} />
                        </BlockShell>
                      </td>
                    </tr>
                  )}

                  <tr>
                    <td style={{ background: 'var(--container)' }}>
                      {primaryBlocks.map((block) => {
                        if (block.id === BLOCK_ID.description && !eventDescription) return null;

                        if (block.id === INFO_CARD_BLOCK_ID) {
                          if (!sessionTimestamp || !timezone) return null;
                          return (
                            <BlockShell
                              key={block.id}
                              block={block}
                              isEditMode={!!isEditMode}
                              isFirst={block.id === primaryBlocks[0]?.id}
                              isLast={block.id === primaryBlocks[primaryBlocks.length - 1]?.id}
                              selected={block.id === selectedBlockId}
                              onSelect={onSelectBlock}
                              onDelete={onDeleteBlock}
                              onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                              onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                            >
                              <StyledBlockBox
                                blockId={block.id}
                                style={block.style}
                                defaults={{ margin: '12px 56px 32px 56px' }}
                                align="right"
                                fitContent
                              >
                                <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                                  <tbody>
                                    <tr>
                                      <td style={infoRowStyle}>
                                        <b>Date</b> — {sessionDateLabel}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={infoRowStyle}>
                                        <b>Time</b> — {sessionTimeLabel}
                                      </td>
                                    </tr>
                                    {durationMinutes !== null && (
                                      <tr>
                                        <td style={infoRowStyle}>
                                          <b>Duration</b> — {durationMinutes} Minutes
                                        </td>
                                      </tr>
                                    )}
                                    <tr>
                                      <td
                                        style={{
                                          color: 'var(--heading)',
                                          fontSize: '18px',
                                          fontFamily: 'var(--body-font)',
                                        }}
                                      >
                                        <b>Topic</b> — {event.name}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </StyledBlockBox>
                            </BlockShell>
                          );
                        }

                        return (
                          <BlockShell
                            key={block.id}
                            block={block}
                            isEditMode={!!isEditMode}
                            isFirst={block.id === primaryBlocks[0]?.id}
                            isLast={block.id === primaryBlocks[primaryBlocks.length - 1]?.id}
                            selected={block.id === selectedBlockId}
                            onSelect={onSelectBlock}
                            onDelete={onDeleteBlock}
                            onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                            onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                          >
                            <BlockRenderer
                              block={block}
                              stylePreset={PALAIS_BLOCK_PRESETS[block.id]}
                              onValueChange={onUpdateBlockValue}
                              onImageChange={onUpdateBlockImage}
                              context={blockContext}
                            />
                          </BlockShell>
                        );
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td align="center" style={{ padding: '24px 0' }}>
                        <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Palais"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--heading': heading,
          '--body': body,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px', background: 'var(--container)' }}
            >
              <tbody>
                <tr>
                  <td height={20} style={{ background: 'var(--container)', fontSize: 0, lineHeight: '0' }}>
                    &nbsp;
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: '0 56px' }}>
                    <div style={{ borderTop: '2px solid var(--heading)', opacity: 1, fontSize: 0, lineHeight: '0' }}>
                      &nbsp;
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HeaderWidget
                      value={_salutation}
                      setValue={setSalutation}
                      color="var(--heading)"
                      fontFamily="var(--body-font)"
                      fontSize="16px"
                      fontWeight={400}
                      textTransform="uppercase"
                      letterSpacing="1px"
                      textAlign="center"
                      whiteSpace="nowrap"
                      padding="16px 56px"
                    />
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '0 56px' }}>
                    <div style={{ borderTop: '2px solid var(--heading)', opacity: 1, fontSize: 0, lineHeight: '0' }}>
                      &nbsp;
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <HeaderWidget
                      value={eventTitle}
                      setValue={setTitle}
                      color="var(--heading)"
                      fontFamily="var(--heading-font)"
                      fontSize="52px"
                      fontWeight={400}
                      fontStyle="italic"
                      lineHeight="57px"
                      padding="20px 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <ImageWidget
                      src={bodyImg}
                      setImage={setMainImage}
                      size="original"
                      width={244}
                      height={306}
                      borderRadius={imageRadius}
                      padding="12px 56px"
                      align="left"
                    />
                  </td>
                </tr>

                {sessionTimestamp && (
                  <tr>
                    <td align="right" style={{ padding: '12px 56px 32px 56px' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td
                              style={{
                                color: 'var(--heading)',
                                fontSize: '18px',
                                fontFamily: 'var(--body-font)',
                                paddingBottom: '16px',
                              }}
                            >
                              <b>Date</b> — {sessionDateLabel}
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                color: 'var(--heading)',
                                fontSize: '18px',
                                fontFamily: 'var(--body-font)',
                                paddingBottom: '16px',
                              }}
                            >
                              <b>Time</b> — {sessionTimeLabel}
                            </td>
                          </tr>
                          {durationMinutes !== null && (
                            <tr>
                              <td
                                style={{
                                  color: 'var(--heading)',
                                  fontSize: '18px',
                                  fontFamily: 'var(--body-font)',
                                  paddingBottom: '16px',
                                }}
                              >
                                <b>Duration</b> — {durationMinutes} Minutes
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td style={{ color: 'var(--heading)', fontSize: '18px', fontFamily: 'var(--body-font)' }}>
                              <b>Topic</b> — {event.name}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}

                <tr>
                  <td style={{ padding: '0 56px' }}>
                    <div style={{ borderTop: '2px solid var(--accent)', fontSize: 0, lineHeight: '0' }}>&nbsp;</div>
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: '20px 56px' }}>
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            style={{
                              background: 'var(--button-color)',
                              border: '2px solid var(--accent)',
                              borderRadius: buttonRadius,
                              padding: '12px 24px',
                            }}
                          >
                            <a
                              href={anchorHref}
                              onClick={(e) => e.preventDefault()}
                              style={{ textDecoration: 'none', display: 'inline-block' }}
                            >
                              <ContentEditable
                                value={_cta}
                                setValue={setCta}
                                style={{
                                  color: 'var(--button-text)',
                                  fontSize: '16px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'nowrap',
                                }}
                              />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

                {eventDescription && (
                  <tr>
                    <td>
                      <ParagraphWidget
                        value={eventDescription}
                        setValue={setDescription}
                        color="var(--body)"
                        fontFamily="var(--body-font)"
                        fontSize="14px"
                        fontStyle="italic"
                        lineHeight="22px"
                        padding="32px 56px"
                      />
                    </td>
                  </tr>
                )}

                <tr>
                  <td style={{ padding: '0 56px' }}>
                    <div style={{ borderTop: '2px solid var(--heading)', opacity: 0.3, fontSize: 0, lineHeight: '0' }}>
                      &nbsp;
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <ParagraphWidget
                      value={DEFAULTS.taglineText}
                      color="var(--heading)"
                      fontFamily="var(--body-font)"
                      fontSize="12px"
                      textTransform="uppercase"
                      textAlign="center"
                      padding="8px 56px 40px 56px"
                    />
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
